import React, { useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useTranslation } from '@/components/utils/translations';
import { isDemarqueFamilyTenant, isBibliothecaFamilyTenant } from '@/lib/tenantUtils';

export default function MarketShare({ accounts, currentTenant, market }) {
  const { t } = useTranslation();
  
  // Determine categories based on tenant
  const categories = useMemo(() => {
    if (isDemarqueFamilyTenant(currentTenant) || isBibliothecaFamilyTenant(currentTenant)) {
      return {
        'Library Providers': 'library_product'
      };
    }
    if (currentTenant === 'skillwell') {
      return {
        'LMS': 'lms_vendor',
        'Immersive': 'immersive_vendor',
        'Authoring': 'authoring_product'
      };
    }
    if (currentTenant === 'kairadigital') {
      return {
        'Planning / Control Tower': 'tower_product',
        'SIS': 'sis_vendor',
        'RTTV / Visibility': 'rttv_product'
      };
    }
    if (currentTenant === 'wayground') {
      return {
        'LMS': 'lms_vendor',
        'Assessment': 'assessment_vendor'
      };
    }
    if (currentTenant === 'plataformaa') {
      return {
        'LMS': 'lms_vendor',
        'Assessment': 'assessment_vendor',
        'Immersive': 'immersive_vendor'
      };
    }
    if (currentTenant === 'learnwise') {
      return {
        'LMS': 'lms_vendor',
        'Feedback': 'feedback_vendor',
        'Tutor': 'tutor_vendor',
        'Support': 'support_vendor'
      };
    }
    if (currentTenant === 'gravyty') {
      return {
        'LMS': 'lms_vendor',
        'Feedback': 'feedback_vendor',
        'Tutor': 'tutor_vendor',
        'Support': 'support_vendor'
      };
    }
    if (currentTenant === 'infonetica' || currentTenant === 'cayuse') {
      return {
        'LMS': 'lms_vendor',
        'SIS': 'sis_vendor',
        'Research': 'research_vendor'
      };
    }
    if (currentTenant === 'almentor') {
      return {
        'LMS': 'lms_vendor',
        'SIS': 'sis_vendor',
        'Library': 'library_vendor'
      };
    }
    if (currentTenant === 'uplanner') {
      return {
        'LMS': 'lms_vendor',
        'SIS': 'sis_vendor',
        'Degree Planning': 'degree_planning_vendor'
      };
    }
    if (currentTenant === 'calmedica') {
      return {
        'EHR': 'ehr_vendor',
        'iPass': 'ipass_vendor',
        'Patient Portal': 'patientportal_vendor'
      };
    }
    if (currentTenant === 'liaison') {
      return {
        'LMS': 'lms_vendor',
        'SIS': 'sis_vendor',
        'Admissions': 'admissions_vendor'
      };
    }
    return {
      'LMS': 'lms_vendor',
      'SIS': 'sis_vendor',
      'ePortfolio': 'eportfolio_vendor'
    };
  }, [currentTenant]);

  const [category, setCategory] = React.useState(Object.keys(categories)[0]);
  
  const isUK = market === 'united-kingdom';
  
  // Calculate actual market share from account data
  const marketShareData = useMemo(() => {
    if (!accounts || accounts.length === 0) {
      return {};
    }

    const result = {};
    
    Object.entries(categories).forEach(([categoryName, productField]) => {
      const productCounts = {};
      let total = 0;
      
      accounts.forEach(acc => {
        let product = acc[productField];
        if (product && product !== 'N/A' && product !== 'Unknown' && product !== 'None') {
          // Consolidate D2L variants
          if (product === 'D2L Corp.' || product === 'D2L Corp') {
            product = 'D2L Brightspace';
          }
          
          // Consolidate Blackboard variants - all become "Blackboard Learn"
          if (product === 'Blackboard Learn.' || product === 'Blackboard Ultra') {
            product = 'Blackboard Learn';
          }
          
          // Learnwise LMS: split "Moodle, Teams" into separate tallies (Moodle +1, Teams +1)
          const productsToCount = ((currentTenant === 'learnwise' || currentTenant === 'gravyty') && productField === 'lms_vendor' && product.includes(','))
            ? product.split(',').map(p => p.trim()).filter(Boolean)
            : [product];
          
          productsToCount.forEach(p => {
            if (p && p !== 'N/A' && p !== 'Unknown' && p !== 'None') {
              productCounts[p] = (productCounts[p] || 0) + 1;
              total++;
            }
          });
        }
      });
      
      const sorted = Object.entries(productCounts)
        .map(([name, count]) => ({
          name,
          share: total > 0 ? Math.round((count / total) * 100) : 0
        }))
        .sort((a, b) => b.share - a.share);
      const top5 = sorted.slice(0, 5);
      const top5Sum = top5.reduce((s, x) => s + x.share, 0);
      const otherShare = Math.max(0, 100 - top5Sum);
      result[categoryName] = otherShare > 0
        ? [...top5, { name: 'Other', share: otherShare }]
        : top5;
    });

    return result;
  }, [accounts, categories, currentTenant]);

  const data = marketShareData[category] || [];
  
  return (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <div className="flex justify-between items-center">
            <CardTitle className="text-lg font-semibold text-[#0A1A2F]">{t('marketShare')}</CardTitle>
            <div className="flex items-center gap-2">
                {Object.keys(categories).map(cat => (
                  <button 
                    key={cat}
                    onClick={() => setCategory(cat)} 
                    className={`text-xs px-3 py-1 rounded-full ${category === cat ? 'bg-[#00C2C2] text-white' : 'bg-gray-100'}`}
                  >
                    {cat}
                  </button>
                ))}
            </div>
        </div>
        {isUK && (
          <p className="text-xs text-[#5C6B7A] italic mt-1">(data is based on 16 non-Canvas universities)</p>
        )}
      </CardHeader>
      <CardContent>
        {data.length > 0 ? (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} strokeOpacity={0.2} />
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} stroke="#5C6B7A" width={80} />
                <Tooltip formatter={(value) => `${value}%`} />
                <Bar dataKey="share" fill="#00C2C2" background={{ fill: '#eee' }} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-500">
            <p>No {category} data available for current accounts</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}